import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Search, ChevronDown, ChevronUp } from 'lucide-react';
import { commissions } from '../../data/mock';
import Layout from '../../components/layout/Layout';

const CommissionsPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const filteredCommissions = commissions.filter(commission =>
    commission.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    commission.president.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-20 bg-[#0A1628] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 bg-blue-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-blue-400 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <nav className="flex items-center gap-2 text-sm text-gray-400 mb-6">
              <Link to="/" className="hover:text-white transition-colors">Inicio</Link>
              <span>/</span>
              <Link to="/acerca-de/historia" className="hover:text-white transition-colors">Acerca de</Link>
              <span>/</span>
              <span className="text-blue-400">Comisiones</span>
            </nav>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Comisiones de Trabajo
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Conoce nuestras comisiones especializadas que integran el talento de socios voluntarios para la formulación de posicionamientos de COPARMEX.
            </p>
          </div>
        </div>
      </section>

      {/* What is a Commission */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-[#1E40AF]/5 to-transparent rounded-2xl p-8 border-l-4 border-[#1E40AF]">
            <h2 className="text-2xl font-bold text-[#0A1628] mb-4">¿Qué es una Comisión de Trabajo?</h2>
            <p className="text-gray-600 leading-relaxed">
              Una comisión de trabajo es un órgano de estudio y consulta especializada que integra el talento de socios voluntarios, funcionarios y en su caso expertos externos para formulación de los posicionamientos de COPARMEX. Las comisiones son el espacio natural para que los socios de los centros empresariales participen de manera libre y activa en el análisis, reflexión, propuesta y acción en torno a determinados temas.
            </p>
          </div>
        </div>
      </section>

      {/* Search */}
      <section className="py-8 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative max-w-md">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar comisión o presidente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E40AF]/20 focus:border-[#1E40AF] transition-all"
            />
          </div>
        </div>
      </section>

      {/* Commissions Grid */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCommissions.map((commission) => (
              <div
                key={commission.id}
                className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 group"
              >
                <div className="p-6">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#1E40AF]/10 rounded-xl flex items-center justify-center group-hover:bg-[#1E40AF] transition-colors">
                      <Users className="h-6 w-6 text-[#1E40AF] group-hover:text-white transition-colors" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-[#0A1628] mb-1 group-hover:text-[#1E40AF] transition-colors">
                        {commission.name}
                      </h3>
                      <p className="text-sm text-gray-500">{commission.president}</p>
                    </div>
                  </div>
                  
                  <div className="relative">
                    <p className={`text-gray-600 text-sm leading-relaxed ${
                      expandedId === commission.id ? '' : 'line-clamp-3'
                    }`}>
                      {commission.description}
                    </p>
                    
                    {commission.description.length > 150 && (
                      <button
                        onClick={() => setExpandedId(expandedId === commission.id ? null : commission.id)}
                        className="mt-2 text-[#1E40AF] text-sm font-medium flex items-center gap-1 hover:gap-2 transition-all"
                      >
                        {expandedId === commission.id ? (
                          <>
                            Ver menos
                            <ChevronUp className="h-4 w-4" />
                          </>
                        ) : (
                          <>
                            Ver más
                            <ChevronDown className="h-4 w-4" />
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Presidente de Comisión</span>
                    <span className="inline-flex items-center px-3 py-1 bg-[#1E40AF]/10 text-[#1E40AF] text-xs font-medium rounded-full">
                      Activa
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredCommissions.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <Search className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No se encontraron comisiones</h3>
              <p className="text-gray-500">Intenta con otros términos de búsqueda</p>
            </div>
          )}
        </div>
      </section>

      {/* Join CTA */}
      <section className="py-16 bg-gradient-to-br from-[#1E40AF] to-[#0A1628]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            ¿Quieres participar en una comisión?
          </h2>
          <p className="text-blue-100 mb-8">
            Como socio de COPARMEX Manzanillo puedes participar activamente en las comisiones de tu interés
          </p>
          <Link
            to="/contacto"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#1E40AF] font-medium rounded-lg hover:bg-gray-100 transition-all duration-300"
          >
            Contáctanos
            <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default CommissionsPage;
